import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle, X, ClipboardText, FunnelSimple, Copy } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  late_arrival: { l: 'Late Arrival', bg: '#fef3c7', fg: '#92400e' },
  early_leave: { l: 'Early Leave', bg: '#fed7aa', fg: '#9a3412' },
  custom_hours: { l: 'Custom Hours', bg: '#dbeafe', fg: '#1e40af' },
  short_permission: { l: 'Short Permission', bg: '#e0e7ff', fg: '#3730a3' },
  wfh: { l: 'WFH', bg: '#dcfce7', fg: '#166534' },
  other: { l: 'Other', bg: '#f3f4f6', fg: '#374151' },
};
const STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-rose-100 text-rose-800',
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const thisMonthStr = () => new Date().toISOString().slice(0, 7);

// Compute the ISO start/end (YYYY-MM-DD) for the current window mode relative to today.
const trackingWindow = (mode, custom = {}) => {
  const today = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  if (mode === 'day') {
    const t = iso(today);
    return { from: t, to: t };
  }
  if (mode === 'week') {
    const d = new Date(today);
    const dow = (d.getDay() + 6) % 7; // 0 = Monday
    const start = new Date(d); start.setDate(d.getDate() - dow);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return { from: iso(start), to: iso(end) };
  }
  if (mode === 'month') {
    const y = today.getFullYear();
    const m = today.getMonth();
    return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
  }
  if (mode === 'quarter') {
    const y = today.getFullYear();
    const q = Math.floor(today.getMonth() / 3);
    return { from: iso(new Date(y, q * 3, 1)), to: iso(new Date(y, q * 3 + 3, 0)) };
  }
  if (mode === 'year') {
    const y = today.getFullYear();
    return { from: iso(new Date(y, 0, 1)), to: iso(new Date(y, 11, 31)) };
  }
  if (mode === 'custom') {
    // custom = {from, to}
    const from = custom.from || iso(today);
    const to = custom.to || iso(today);
    return { from, to };
  }
  // Fallback: this month
  const y = today.getFullYear();
  const m = today.getMonth();
  return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
};

export default function PermissionBoardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list'); // 'list' | 'tracking'
  const [trackingMode, setTrackingMode] = useState('month'); // day | week | month | quarter | year | custom
  const [trackingCustom, setTrackingCustom] = useState({ from: '', to: '' });
  const [trackingItems, setTrackingItems] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [filter, setFilter] = useState({
    employee_id: '',
    status: '',
    request_type: '',
    month: thisMonthStr(),
    date: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
      // Server treats month or date — date takes precedence
      if (params.date) delete params.month;
      const [rsList, rsEmps] = await Promise.all([
        axios.get(`${API_URL}/api/permission-requests`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/employees`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setItems(rsList.data || []);
      setEmployees(rsEmps.data || []);
    } catch (e) {
      toast.error('Failed to load: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Tracking tab: pull all requests for the chosen window and aggregate by employee.
  const loadTracking = useCallback(async () => {
    if (activeTab !== 'tracking') return;
    setTrackingLoading(true);
    try {
      const { from, to } = trackingWindow(trackingMode, trackingCustom);
      if (!from || !to) { setTrackingItems([]); setTrackingLoading(false); return; }
      // Use the new from_date/to_date backend params — supports arbitrary ranges
      // including multi-month, quarters and years.
      const res = await axios.get(`${API_URL}/api/permission-requests`, {
        params: { from_date: from, to_date: to },
        withCredentials: true,
      });
      setTrackingItems(res.data || []);
    } catch (e) {
      toast.error('Failed to load tracking: ' + (e.response?.data?.detail || e.message));
    }
    setTrackingLoading(false);
  }, [activeTab, trackingMode, trackingCustom]);

  useEffect(() => { loadTracking(); }, [loadTracking]);

  // Aggregate tracking data: employee_id -> { name, total, pending, approved, rejected, byType }
  const trackingAgg = useMemo(() => {
    const map = {};
    trackingItems.forEach((p) => {
      const key = p.employee_id || '__unknown__';
      if (!map[key]) {
        map[key] = {
          employee_id: key,
          employee_name: p.employee_name || '—',
          total: 0, pending: 0, approved: 0, rejected: 0,
          byType: {}, totalMinutes: 0,
        };
      }
      const row = map[key];
      row.total += 1;
      row[p.status] = (row[p.status] || 0) + 1;
      row.byType[p.request_type] = (row.byType[p.request_type] || 0) + 1;
      row.totalMinutes += Number(p.duration_minutes || 0);
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [trackingItems]);

  const trackingWin = useMemo(() => trackingWindow(trackingMode, trackingCustom), [trackingMode, trackingCustom]);

  const decide = async (p, decision) => {
    if (!window.confirm(`${decision === 'approved' ? 'Approve' : 'Reject'} this permission request for ${p.employee_name}?`)) return;
    try {
      await axios.post(`${API_URL}/api/permission-requests/${p.id}/decide`, { decision }, { withCredentials: true });
      toast.success(`Request ${decision}`);
      load();
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const decideGroup = async (groupId, members, decision) => {
    if (!window.confirm(`${decision === 'approved' ? 'Approve' : 'Reject'} ALL ${members.length} pending requests in this group?`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/permission-requests/group/${groupId}/decide`, { decision }, { withCredentials: true });
      toast.success(`${r.data.modified || 0} request(s) ${decision}`);
      load();
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete this permission request?`)) return;
    try {
      await axios.delete(`${API_URL}/api/permission-requests/${p.id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  // ── Bulk selection state ───────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState({});   // { id: true }
  const [bulkBusy, setBulkBusy] = useState(false);

  // Reset selection whenever the filtered list changes underneath us.
  useEffect(() => { setSelectedIds({}); }, [items]);

  const pendingItems = useMemo(() => items.filter((i) => i.status === 'pending'), [items]);
  const selectedCount = useMemo(
    () => Object.entries(selectedIds).filter(([id, v]) => v && pendingItems.some((p) => p.id === id)).length,
    [selectedIds, pendingItems],
  );
  const allPendingSelected = pendingItems.length > 0 && pendingItems.every((p) => selectedIds[p.id]);

  const toggleRow = (id) => setSelectedIds((s) => ({ ...s, [id]: !s[id] }));
  const toggleAllPending = (checked) => {
    if (!checked) { setSelectedIds({}); return; }
    const next = {};
    pendingItems.forEach((p) => { next[p.id] = true; });
    setSelectedIds(next);
  };

  const bulkDecide = async (decision) => {
    const ids = Object.entries(selectedIds).filter(([id, v]) => v && pendingItems.some((p) => p.id === id)).map(([id]) => id);
    if (ids.length === 0) { toast.error('Nothing selected'); return; }
    if (!window.confirm(`${decision === 'approved' ? 'Approve' : 'Reject'} ${ids.length} pending request${ids.length === 1 ? '' : 's'}?`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/permission-requests/bulk-decide`, { ids, decision }, { withCredentials: true });
      toast.success(`${r.data.modified || 0} request${(r.data.modified || 0) === 1 ? '' : 's'} ${decision}`);
      setSelectedIds({});
      await load();
    } catch (e) { toast.error('Bulk action failed: ' + (e.response?.data?.detail || e.message)); }
    setBulkBusy(false);
  };

  const stats = useMemo(() => {
    const t = { pending: 0, approved: 0, rejected: 0, total: items.length };
    items.forEach((i) => { t[i.status] = (t[i.status] || 0) + 1; });
    return t;
  }, [items]);

  const publicUrl = useMemo(() => `${window.location.origin}/permission-request`, []);

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success('Public link copied!');
  };

  const sendTestEmail = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/admin/test-email`, {}, { withCredentials: true });
      toast.success(`Test email sent to ${r.data.to}${r.data.cc ? ` (cc ${r.data.cc})` : ''}`);
    } catch (e) {
      toast.error('Email test failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  const TypeChip = ({ t }) => {
    const c = TYPE_LABELS[t] || TYPE_LABELS.other;
    return (
      <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full"
        style={{ background: c.bg, color: c.fg }}>{c.l}</span>
    );
  };

  return (
    <div className="space-y-4" data-testid="permission-board-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
            <ClipboardText size={22} weight="duotone" /> Permission Board
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Track and approve permission requests · Office hours 10:30 AM – 7:30 PM</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline truncate max-w-[300px]" data-testid="public-link">{publicUrl}</a>
          <Button size="sm" variant="outline" onClick={copyLink} data-testid="copy-link-btn">
            <Copy size={14} className="mr-1" /> Copy public link
          </Button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={sendTestEmail} data-testid="test-email-btn">
              ✉️ Test email config
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b" style={{ borderColor: 'var(--dx-border)' }} data-testid="permission-board-tabs">
        <button
          type="button"
          onClick={() => setActiveTab('list')}
          className={`text-sm font-semibold px-4 py-2 -mb-px border-b-2 transition ${activeTab === 'list' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          data-testid="tab-list"
        >
          📋 Requests
        </button>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setActiveTab('tracking')}
            className={`text-sm font-semibold px-4 py-2 -mb-px border-b-2 transition ${activeTab === 'tracking' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid="tab-tracking"
          >
            📊 Tracking
          </button>
        )}
      </div>

      {activeTab === 'tracking' && isAdmin ? (
        <div className="space-y-4" data-testid="tracking-pane">
          {/* Window toggle + custom range */}
          <Card className="p-3 space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-xs font-bold uppercase tracking-wider text-gray-600">Window</div>
              <div className="inline-flex rounded-md overflow-hidden border" data-testid="tracking-mode-toggle">
                {[
                  { v: 'day', l: 'Day' },
                  { v: 'week', l: 'Week' },
                  { v: 'month', l: 'Month' },
                  { v: 'quarter', l: 'Quarter' },
                  { v: 'year', l: 'Year' },
                  { v: 'custom', l: 'Custom' },
                ].map((m) => (
                  <button
                    key={m.v}
                    type="button"
                    onClick={() => {
                      setTrackingMode(m.v);
                      // Seed custom range with the CURRENT window on switch so users don't start blank
                      if (m.v === 'custom' && !trackingCustom.from) {
                        setTrackingCustom({ from: trackingWin.from, to: trackingWin.to });
                      }
                    }}
                    className={`text-xs font-semibold px-3 py-1.5 transition ${trackingMode === m.v ? 'text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    style={trackingMode === m.v ? { background: '#1a2e4a' } : undefined}
                    data-testid={`tracking-mode-${m.v}`}
                  >
                    {m.l}
                  </button>
                ))}
              </div>
              <div className="text-xs text-gray-500 ml-auto">
                {trackingWin.from === trackingWin.to ? `On ${trackingWin.from}` : `${trackingWin.from} → ${trackingWin.to}`}
                <span className="ml-2 text-gray-700 font-semibold" data-testid="tracking-total-count">{trackingItems.length} requests</span>
              </div>
            </div>
            {trackingMode === 'custom' && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t" data-testid="tracking-custom-controls">
                <span className="text-[11px] text-gray-500 font-semibold">From</span>
                <input type="date" className="text-xs border rounded px-2 py-1 bg-white"
                  value={trackingCustom.from}
                  onChange={(e) => setTrackingCustom({ ...trackingCustom, from: e.target.value })}
                  data-testid="tracking-from" />
                <span className="text-[11px] text-gray-500 font-semibold">→ To</span>
                <input type="date" className="text-xs border rounded px-2 py-1 bg-white"
                  value={trackingCustom.to}
                  onChange={(e) => setTrackingCustom({ ...trackingCustom, to: e.target.value })}
                  data-testid="tracking-to" />
                <span className="text-[11px] text-gray-400 ml-3">Presets:</span>
                {[
                  { l: 'Last 3 mo', months: 3 },
                  { l: 'Last 6 mo', months: 6 },
                  { l: '1 year', months: 12 },
                  { l: 'Year to date', ytd: true },
                ].map((preset) => (
                  <button
                    key={preset.l}
                    onClick={() => {
                      const today = new Date();
                      const iso = (d) => d.toISOString().slice(0, 10);
                      let from;
                      const to = iso(today);
                      if (preset.ytd) {
                        from = iso(new Date(today.getFullYear(), 0, 1));
                      } else {
                        const d = new Date(today);
                        d.setMonth(d.getMonth() - preset.months + 1);
                        d.setDate(1);
                        from = iso(d);
                      }
                      setTrackingCustom({ from, to });
                    }}
                    className="text-[11px] px-2 py-1 rounded border hover:bg-gray-50 font-semibold text-gray-600"
                    data-testid={`preset-${preset.l.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {preset.l}
                  </button>
                ))}
              </div>
            )}
          </Card>

          {/* Aggregation table */}
          <Card className="p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead style={{ background: 'var(--dx-bg)' }}>
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-xs uppercase">#</th>
                  <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Employee</th>
                  <th className="text-center px-3 py-2 font-semibold text-xs uppercase">Total</th>
                  <th className="text-center px-3 py-2 font-semibold text-xs uppercase" style={{ background: '#fef3c7', color: '#92400e' }}>Late Arrival</th>
                  <th className="text-center px-3 py-2 font-semibold text-xs uppercase" style={{ background: '#fed7aa', color: '#9a3412' }}>Early Leave</th>
                  <th className="text-center px-3 py-2 font-semibold text-xs uppercase">Pending</th>
                  <th className="text-center px-3 py-2 font-semibold text-xs uppercase">Approved</th>
                  <th className="text-center px-3 py-2 font-semibold text-xs uppercase">Rejected</th>
                  <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Other Types</th>
                  <th className="text-right px-3 py-2 font-semibold text-xs uppercase">Short Perm. Min</th>
                </tr>
              </thead>
              <tbody>
                {trackingLoading ? (
                  <tr><td colSpan={10} className="text-center py-8 text-gray-500">Loading…</td></tr>
                ) : trackingAgg.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-8 text-gray-500" data-testid="tracking-empty">No requests in this window.</td></tr>
                ) : trackingAgg.map((row, idx) => (
                  <tr key={row.employee_id} className="border-t hover:bg-gray-50" data-testid={`tracking-row-${row.employee_id}`}>
                    <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                    <td className="px-3 py-2 font-medium" data-testid={`tracking-name-${row.employee_id}`}>{row.employee_name}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-xs font-bold bg-blue-100 text-blue-800" data-testid={`tracking-total-${row.employee_id}`}>
                        {row.total}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center" style={{ background: row.byType.late_arrival ? '#fef9c3' : 'transparent' }}>
                      {row.byType.late_arrival
                        ? <span className="text-sm font-bold text-amber-800" data-testid={`tracking-late-${row.employee_id}`}>{row.byType.late_arrival}</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center" style={{ background: row.byType.early_leave ? '#ffedd5' : 'transparent' }}>
                      {row.byType.early_leave
                        ? <span className="text-sm font-bold text-orange-800" data-testid={`tracking-early-${row.employee_id}`}>{row.byType.early_leave}</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {row.pending ? <span className="text-xs font-bold text-amber-700">{row.pending}</span> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {row.approved ? <span className="text-xs font-bold text-emerald-700">{row.approved}</span> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {row.rejected ? <span className="text-xs font-bold text-rose-700">{row.rejected}</span> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(row.byType)
                          .filter(([t]) => t !== 'late_arrival' && t !== 'early_leave')
                          .map(([t, n]) => {
                            const c = TYPE_LABELS[t] || TYPE_LABELS.other;
                            return (
                              <span key={t} className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: c.bg, color: c.fg }}>
                                {c.l} · {n}
                              </span>
                            );
                          })}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-gray-600">
                      {row.totalMinutes ? `${row.totalMinutes} min` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      ) : (
      <>
      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Total</div>
          <div className="text-2xl font-bold mt-0.5" style={{ color: '#1a2e4a' }}>{stats.total}</div>
        </Card>
        <Card className="p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Pending</div>
          <div className="text-2xl font-bold mt-0.5 text-amber-700">{stats.pending}</div>
        </Card>
        <Card className="p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Approved</div>
          <div className="text-2xl font-bold mt-0.5 text-emerald-700">{stats.approved}</div>
        </Card>
        <Card className="p-3">
          <div className="text-[11px] uppercase tracking-wider text-gray-500">Rejected</div>
          <div className="text-2xl font-bold mt-0.5 text-rose-700">{stats.rejected}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-wider text-gray-600">
          <FunnelSimple size={14} /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <div>
            <Label className="text-[10px]">Employee</Label>
            <select className="w-full border rounded px-2 py-1.5 text-xs bg-white" value={filter.employee_id}
              onChange={(e) => setFilter({ ...filter, employee_id: e.target.value })} data-testid="filter-employee">
              <option value="">All employees</option>
              {employees.filter((e) => e.is_active !== false).map((e) => (
                <option key={e.id} value={e.id}>{e.name} ({e.emp_id})</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px]">Status</Label>
            <select className="w-full border rounded px-2 py-1.5 text-xs bg-white" value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="filter-status">
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px]">Type</Label>
            <select className="w-full border rounded px-2 py-1.5 text-xs bg-white" value={filter.request_type}
              onChange={(e) => setFilter({ ...filter, request_type: e.target.value })} data-testid="filter-type">
              <option value="">All types</option>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.l}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px]">Month</Label>
            <Input type="month" value={filter.month} onChange={(e) => setFilter({ ...filter, month: e.target.value, date: '' })} data-testid="filter-month" />
          </div>
          <div>
            <Label className="text-[10px]">Specific date</Label>
            <Input type="date" value={filter.date} onChange={(e) => setFilter({ ...filter, date: e.target.value })} data-testid="filter-date" />
          </div>
        </div>
        <div className="flex justify-end mt-2 gap-2">
          <Button size="sm" variant="outline" onClick={() => setFilter({ employee_id: '', status: '', request_type: '', month: thisMonthStr(), date: '' })} data-testid="filter-reset">Reset</Button>
          <Button size="sm" variant="outline" onClick={() => setFilter({ ...filter, date: todayStr(), month: '' })} data-testid="filter-today">Today</Button>
        </div>
      </Card>

      {/* Sticky bulk action bar — appears when 1+ pending requests are selected */}
      {isAdmin && selectedCount > 0 && (
        <div
          className="sticky top-2 z-30 flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg shadow-lg border border-indigo-200 bg-white"
          data-testid="perm-bulk-bar"
        >
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-700">
              {selectedCount} pending selected
            </span>
            <button
              type="button"
              onClick={() => setSelectedIds({})}
              className="text-[11px] text-gray-500 hover:text-gray-700 underline"
              data-testid="perm-bulk-clear"
            >
              Clear
            </button>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-green-700 border-green-400 bg-green-50 hover:bg-green-100"
              disabled={bulkBusy}
              onClick={() => bulkDecide('approved')}
              data-testid="perm-bulk-approve"
            >
              <CheckCircle size={14} className="mr-1" weight="fill" />
              {bulkBusy ? 'Working…' : `Approve ${selectedCount}`}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-red-700 border-red-400 bg-red-50 hover:bg-red-100"
              disabled={bulkBusy}
              onClick={() => bulkDecide('rejected')}
              data-testid="perm-bulk-reject"
            >
              <X size={14} className="mr-1" />
              {bulkBusy ? 'Working…' : `Reject ${selectedCount}`}
            </Button>
          </div>
        </div>
      )}

      {/* Requests table */}
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead style={{ background: 'var(--dx-bg)' }}>
            <tr>
              {isAdmin && (
                <th className="text-center px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allPendingSelected}
                    disabled={pendingItems.length === 0}
                    onChange={(e) => toggleAllPending(e.target.checked)}
                    data-testid="perm-select-all"
                    title={pendingItems.length === 0 ? 'No pending requests' : 'Select / deselect all pending requests'}
                  />
                </th>
              )}
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Employee</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Type</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Date</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Hours</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Reason</th>
              <th className="text-left px-3 py-2 font-semibold text-xs uppercase">Status</th>
              <th className="text-right px-3 py-2 font-semibold text-xs uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-gray-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={isAdmin ? 8 : 7} className="text-center py-8 text-gray-500">No requests match these filters.</td></tr>
            ) : items.map((p) => {
              const groupMembers = p.group_id ? items.filter((x) => x.group_id === p.group_id) : [];
              const pendingInGroup = groupMembers.filter((x) => x.status === 'pending');
              const showGroupActions = isAdmin && p.group_id && pendingInGroup.length > 1 && pendingInGroup[0].id === p.id;
              const isPending = p.status === 'pending';
              return (
              <tr key={p.id} className={`border-t hover:bg-gray-50 ${selectedIds[p.id] ? 'bg-indigo-50/50' : ''}`} data-testid={`perm-row-${p.id}`}>
                {isAdmin && (
                  <td className="px-2 py-2 text-center align-middle">
                    <input
                      type="checkbox"
                      checked={!!selectedIds[p.id]}
                      onChange={() => toggleRow(p.id)}
                      disabled={!isPending}
                      data-testid={`perm-check-${p.id}`}
                      title={isPending ? '' : 'Only pending requests can be bulk-actioned'}
                    />
                  </td>
                )}
                <td className="px-3 py-2 font-medium">
                  <div>{p.employee_name || '—'}</div>
                  {p.group_id && (
                    <span className="inline-block text-[8px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 mt-0.5">
                      GROUP · {groupMembers.length}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2"><TypeChip t={p.request_type} /></td>
                <td className="px-3 py-2 text-[12px]">{p.date}</td>
                <td className="px-3 py-2 text-[11px] text-gray-600">
                  {p.request_type === 'short_permission' && p.duration_minutes
                    ? <strong>{p.duration_minutes} min</strong>
                    : (p.requested_start || p.requested_end) ? `${p.requested_start || '?'} – ${p.requested_end || '?'}` : '—'}
                </td>
                <td className="px-3 py-2 text-[11px] text-gray-700 max-w-xs truncate" title={p.reason}>{p.reason}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${STATUS_COLOR[p.status] || 'bg-gray-100'}`}>
                    {(p.status || '').toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {isAdmin && p.status === 'pending' && (
                    <div className="flex flex-col items-end gap-1">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="text-green-700 border-green-300" onClick={() => decide(p, 'approved')} data-testid={`approve-${p.id}`}>
                          <CheckCircle size={14} className="mr-1" weight="fill" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => decide(p, 'rejected')} data-testid={`reject-${p.id}`}>
                          <X size={14} className="mr-1" /> Reject
                        </Button>
                      </div>
                      {showGroupActions && (
                        <div className="flex gap-1 mt-1">
                          <button
                            onClick={() => decideGroup(p.group_id, pendingInGroup, 'approved')}
                            className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-green-100 text-green-800 hover:bg-green-200"
                            data-testid={`approve-group-${p.group_id}`}
                          >
                            ✓ Approve Group ({pendingInGroup.length})
                          </button>
                          <button
                            onClick={() => decideGroup(p.group_id, pendingInGroup, 'rejected')}
                            className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-rose-100 text-rose-800 hover:bg-rose-200"
                            data-testid={`reject-group-${p.group_id}`}
                          >
                            ✕ Reject Group
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {isAdmin && p.status !== 'pending' && (
                    <Button size="sm" variant="ghost" onClick={() => remove(p)} className="text-rose-500" data-testid={`delete-${p.id}`}>
                      <X size={14} />
                    </Button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      </>
      )}
    </div>
  );
}
